import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Target, Lightbulb, TrendingUp } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface SessionSummaryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  summary: {
    goal?: string;
    keyInsights: string[];
    patterns: string[];
    nextSteps: string[];
    emotionalJourney?: string;
  };
  onSaveToTimeline?: () => void;
}

export const SessionSummaryDialog: React.FC<SessionSummaryDialogProps> = ({ 
  isOpen, 
  onClose, 
  summary,
  onSaveToTimeline 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Session Summary
          </DialogTitle>
          <DialogDescription>
            Here's what we explored together today
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {summary.goal && (
            <Card className="p-4 bg-primary/5 border-primary/20">
              <div className="flex items-start gap-3">
                <Target className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Your Intention</p>
                  <p className="text-sm text-muted-foreground">{summary.goal}</p>
                </div>
              </div>
            </Card>
          )}

          {summary.keyInsights.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                <p className="text-sm font-medium text-foreground">Key Insights</p>
              </div>
              <div className="space-y-2">
                {summary.keyInsights.map((insight, index) => (
                  <Card key={index} className="p-3">
                    <p className="text-sm text-foreground">{insight}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {summary.patterns.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <p className="text-sm font-medium text-foreground">Patterns Identified</p>
              </div>
              <div className="space-y-2">
                {summary.patterns.map((pattern, index) => (
                  <Card key={index} className="p-3 bg-muted/30">
                    <p className="text-sm text-foreground">{pattern}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {summary.emotionalJourney && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Emotional Journey</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {summary.emotionalJourney}
                </p>
              </div>
            </>
          )}

          {summary.nextSteps.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Suggested Next Steps</p>
                <ul className="space-y-2">
                  {summary.nextSteps.map((step, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-primary mt-0.5">→</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          {onSaveToTimeline && (
            <Button variant="outline" onClick={onSaveToTimeline}>
              Save to Timeline
            </Button>
          )}
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
